import type { Icon } from '@change/ds-icons';
import {
	CDotIcon,
	ChatBubbleIcon,
	EmailIcon,
	FacebookRegularIcon,
	InstagramIcon,
	LineIcon,
	NextDoorIcon,
	RedditIcon,
	TikTokIcon,
	TwitterIcon,
	VkIcon,
	WhatsAppIcon,
} from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import type { IconComponent } from '@change-corgi/design-system-legacy/icons';
import {
	iconLightbulb,
	iconLinkedIn,
	iconLinktree,
	iconQrCode,
	iconSnapchat,
	iconTelegram,
	iconYoutube,
} from '@change-corgi/design-system-legacy/icons';
import type { ColorName } from '@change-corgi/design-system-legacy/theme';

export const OTHER_CHANNEL_KEY = 'other';

type ChannelSetting =
	| {
			iconColor: string;
			translationKey: TranslationKey;
			icon: React.FC<Icon>;
			isLegacyIcon?: false;
	  }
	| {
			iconColor: ColorName;
			translationKey: TranslationKey;
			icon: IconComponent;
			isLegacyIcon: true;
	  };
export const CHANNEL_SETTINGS: Record<string, ChannelSetting> = {
	change_email: {
		iconColor: 'text-typography-brand',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.change_email',
		),
		icon: EmailIcon,
	},
	email: {
		iconColor: 'text-[#842B8F]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.email',
		),
		icon: EmailIcon,
	},
	facebook: {
		iconColor: 'text-[#1877F2]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.facebook',
		),
		icon: FacebookRegularIcon,
	},
	instagram: {
		iconColor: 'text-[#FF0069]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.instagram',
		),
		icon: InstagramIcon,
	},
	line: {
		iconColor: 'text-[#06C755]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.line',
		),
		icon: LineIcon,
	},
	linkedin: {
		iconColor: 'social-linkedin',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.linkedin',
		),
		icon: iconLinkedIn,
		isLegacyIcon: true,
	},
	linktree: {
		iconColor: 'social-linktree',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.linktree',
		),
		icon: iconLinktree,
		isLegacyIcon: true,
	},
	nextdoor: {
		iconColor: 'text-[#00422D]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.nextdoor',
		),
		icon: NextDoorIcon,
	},
	organic_search: {
		iconColor: 'primary-changeRed',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.organic_search',
		),
		icon: iconLightbulb,
		isLegacyIcon: true,
	},
	other: {
		iconColor: 'primary-black',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.other_channel',
		),
		icon: iconLightbulb,
		isLegacyIcon: true,
	},
	promotion: {
		iconColor: 'text-typography-brand',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.promotion',
		),
		icon: CDotIcon,
	},
	qr_code: {
		iconColor: 'primary-black',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.qr_code',
		),
		icon: iconQrCode,
		isLegacyIcon: true,
	},
	reddit: {
		iconColor: 'text-[#FF4500]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.reddit',
		),
		icon: RedditIcon,
	},
	sms: {
		iconColor: 'text-[#528334]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.sms',
		),
		icon: ChatBubbleIcon,
	},
	snapchat: {
		iconColor: 'primary-black',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.snapchat',
		),
		icon: iconSnapchat,
		isLegacyIcon: true,
	},
	telegram: {
		iconColor: 'social-telegram',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.telegram',
		),
		icon: iconTelegram,
		isLegacyIcon: true,
	},
	tiktok: {
		iconColor: 'text-black',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.tiktok',
		),
		icon: TikTokIcon,
	},
	twitter: {
		iconColor: 'text-black',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.twitter',
		),
		icon: TwitterIcon,
	},
	vk: {
		iconColor: 'text-[#0077FF]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.vk',
		),
		icon: VkIcon,
	},
	whatsapp: {
		iconColor: 'text-[#25D366]',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.whatsapp',
		),
		icon: WhatsAppIcon,
	},
	youtube: {
		iconColor: 'social-youtube',
		translationKey: translationKey(
			'corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.channels.youtube',
		),
		icon: iconYoutube,
		isLegacyIcon: true,
	},
};
